// A spreadsheet for the admin (Leon, 2026-10-23, mockup v13
// https://claude.ai/artifact/4bNzei31e1V8FSd7MmG4Dc): select whole rows — a
// click, a drag down, Ctrl and Shift — or a box of cells by dragging across
// columns; a floating bar with one total per money column, Copy (with
// headers), Export and Show only these; filter menus that fit each column;
// with rows selected, a column's value list works on the selection. Plus a
// totals row, group by with subtotals, saved views, search highlight, status
// coloured status words, payment logos and double-click for a cell's full value. Admin-only (the executive
// logs today, GiftPass orders next), so it lives in the app, not packages/ui.
//
// This file owns the state and derives everything once; the rules are pure
// (sheet-table.model, sheet-selection, sheet-group, sheet-view) and the grid,
// toolbar, menu and bar only draw what they are handed.

import { Button } from "@bednbody/ui/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@bednbody/ui/components/popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@bednbody/ui/components/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@bednbody/ui/components/empty";
import {
  CopyIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelSimpleXIcon,
  KeyboardIcon,
  RowsIcon,
} from "@phosphor-icons/react";
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { type SheetColumnType, SheetColumnMenu } from "./SheetColumnMenu";
import { type GridColumn, type LogoMark, type PillTone, SheetGrid } from "./SheetGrid";
import { SheetSelectionBar } from "./SheetSelectionBar";
import { SheetToolbar } from "./SheetToolbar";
import { groupRows } from "./sheet-group";
import {
  type CellRef,
  columnTotals,
  dragTo,
  EMPTY_SELECTION,
  idsWithValues,
  moveActive,
  pressCell,
  type SheetSelection,
  selectAll,
  selectedPart,
  selectionChoices,
  toggleActiveRow,
  toTsv,
  withRows,
} from "./sheet-selection";
import {
  applyFilters,
  autoWidth,
  cellText,
  inferKind,
  type SheetCondition,
  type SheetRow,
  sortRows,
  sumOf,
  valueCounts,
} from "./sheet-table.model";
import {
  filterChips,
  normalizeView,
  ONLY_CHIP,
  type SavedView,
  type SheetView,
  sheetStorage,
  withoutFilters,
} from "./sheet-view";

export interface SheetTableColumn {
  key: string;
  label: string;
  /** Readable text for a stored value (a code → "Completed", a UTC time → Manila). Filters keep the stored value. */
  labelOf?: (value: string) => string;
  /** Shown and summed in pesos. */
  money?: boolean;
  /** A date or timestamp: filtered by Today / This week / This month or a range. */
  date?: boolean;
  /** Monospace: refs, IDs, dates, amounts. */
  mono?: boolean;
  /** A status shown as a coloured word. */
  tone?: (text: string) => PillTone | null;
  /** A payment method with a small logo tile before its name. */
  logo?: (text: string) => LogoMark | null;
  /** How the cell reads on screen only (a shortened ID, a compact time); copy and export keep the full text. */
  cell?: (text: string) => string;
  /** Booking refs: the repeated prefix quiet, the unique code bold. */
  emphasizeTail?: boolean;
  width?: number;
  /** Opens the thing the cell names, e.g. an order on its own page. */
  href?: (text: string) => string | undefined;
  /** Digits that are a label, not an amount (a card's count or batch number): never summed. */
  text?: boolean;
}

export interface SheetTableProps<T extends SheetRow> {
  /** Where this sheet's setup and saved views are remembered, e.g. "exec-logs.bookings". */
  scope: string;
  columns: readonly SheetTableColumn[];
  rows: readonly T[];
  idOf: (row: T) => string;
  /** The money columns the selection bar totals for whole rows (a box totals its own columns). */
  summaryKeys?: readonly string[];
  /** What a row is called in the bar: ["booking", "bookings"]. */
  rowNoun?: readonly [string, string];
  /** The setup a first visit starts from (e.g. this month), before anything is remembered. */
  defaultView?: Partial<SheetView>;
  loading?: boolean;
  emptyTitle: string;
  emptyDescription: string;
  /** Saves rows as a file: whole rows in the log's own column order (`null`), or a box's columns. */
  onExport: (rows: T[], columnKeys: string[] | null) => void;
  /** The page's own controls in the toolbar. */
  extra?: ReactNode;
  /** Compact rows and narrower columns (executive logs, Leon 2026-10-30). */
  dense?: boolean;
}

const peso = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });
const plain = new Intl.NumberFormat("en-PH", { maximumFractionDigits: 2 });
const SHORTCUTS: readonly [string, string][] = [
  ["Click", "Select a row"],
  ["Drag down", "Select several"],
  ["Shift+click", "Add a range"],
  ["Ctrl+click", "Add or remove one"],
  ["Drag sideways", "Select just some columns"],
  ["Double-click", "See a cell in full"],
  ["Right-click", "Show only, hide, group"],
  ["Ctrl+C", "Copy with headers"],
  ["Ctrl+A · Esc", "Select all · clear"],
];
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const STEP: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};
/** A text column with this many different values or fewer gets a value list. */
const CHOICE_LIMIT = 30;

function firstView(storeKey: string, fallback: Partial<SheetView> | undefined): SheetView {
  try {
    if (window.localStorage.getItem(`${storeKey}.current`) !== null)
      return sheetStorage.loadCurrent(storeKey);
  } catch {
    // Blocked storage: start from the default.
  }
  return normalizeView(fallback);
}

export function SheetTable<T extends SheetRow>({
  scope,
  columns,
  rows,
  idOf,
  summaryKeys,
  rowNoun = ["row", "rows"],
  defaultView,
  loading = false,
  emptyTitle,
  emptyDescription,
  onExport,
  extra,
  dense = false,
}: SheetTableProps<T>) {
  const storeKey = `bednbody.sheet.${scope}`;
  const [view, setView] = useState<SheetView>(() => firstView(storeKey, defaultView));
  const [saved, setSaved] = useState<SavedView[]>(() => sheetStorage.loadSaved(storeKey));
  const [activeView, setActiveView] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [selection, setSelection] = useState<SheetSelection>(EMPTY_SELECTION);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuCell, setMenuCell] = useState<CellRef | null>(null);
  const [peek, setPeek] = useState<CellRef | null>(null);
  const dragStart = useRef<CellRef | null>(null);
  const gridArea = useRef<HTMLDivElement>(null);

  // A click anywhere outside the sheet clears the selection (Leon, 2026-10-26:
  // having to hit × on the bar was bad). Clicks in the sheet's own menus,
  // popups and toasts don't count — they render outside it, in portals.
  const hasSelection = selection.rows.size > 0 || selection.box !== null;
  useEffect(() => {
    if (!hasSelection) return;
    const clear = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || gridArea.current?.contains(target)) return;
      if (
        target.closest(
          '[data-slot="popover-content"], [data-slot="context-menu-content"], [role="dialog"], [data-sonner-toaster]',
        )
      ) {
        return;
      }
      setSelection(EMPTY_SELECTION);
    };
    document.addEventListener("pointerdown", clear, true);
    return () => document.removeEventListener("pointerdown", clear, true);
  }, [hasSelection]);

  useEffect(() => sheetStorage.saveCurrent(storeKey, view), [storeKey, view]);

  function edit(next: SheetView) {
    setView(next);
    setActiveView(null);
  }

  // ── derive once ─────────────────────────────────────────────────────────
  const byKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);
  const display = (row: SheetRow, column: { key: string }) => {
    const text = cellText(row[column.key]);
    const labelOf = byKey.get(column.key)?.labelOf;
    return text && labelOf ? labelOf(text) : text;
  };
  const kinds = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.key, column.text ? "text" : inferKind(rows, column.key)]),
      ),
    [columns, rows],
  );
  const types = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column): [string, SheetColumnType] => {
          if (column.date) return [column.key, "date"];
          if (column.money) return [column.key, "money"];
          if (kinds[column.key] === "number") return [column.key, "number"];
          const distinct = new Set(rows.map((row) => cellText(row[column.key]))).size;
          return [column.key, distinct <= CHOICE_LIMIT ? "choice" : "text"];
        }),
      ),
    [columns, rows, kinds],
  );
  const gridColumns = useMemo<GridColumn[]>(
    () =>
      columns
        .filter((column) => !view.hiddenColumns.includes(column.key))
        .map((column) => ({
          key: column.key,
          label: column.label,
          numeric: kinds[column.key] === "number",
          mono: column.mono === true || column.money === true || kinds[column.key] === "number",
          tone: column.tone,
          logo: column.logo,
          cell: column.cell,
          emphasizeTail: column.emphasizeTail,
          href: column.href,
          width:
            view.widths[column.key] ??
            column.width ??
            autoWidth(
              column.label,
              rows.slice(0, 200).map((row) => display(row, column)),
              dense,
            ) +
              // A status dot, or a bold ref code, takes more room than its letters.
              (column.logo ? 32 : 0) +
              (column.emphasizeTail ? 18 : 0),
        })),
    // biome-ignore lint/correctness/useExhaustiveDependencies: `display` depends on `columns` only
    [columns, rows, view.hiddenColumns, view.widths, kinds, dense],
  );
  const money = (key: string) => byKey.get(key)?.money === true;
  const format = (key: string, value: number) =>
    money(key) ? peso.format(value) : plain.format(value);

  const query = search.trim().toLowerCase();
  const shownRows = useMemo(() => {
    const only = view.only ? new Set(view.only) : null;
    const searched = rows.filter(
      (row) =>
        (!only || only.has(idOf(row))) &&
        (!query ||
          gridColumns.some((column) => display(row, column).toLowerCase().includes(query))),
    );
    const filtered = applyFilters(searched, view.hidden, view.conditions);
    return sortRows(filtered, view.sort, view.sort ? (kinds[view.sort.key] ?? "text") : "text");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `display` depends on `columns` only
  }, [rows, idOf, query, gridColumns, view.only, view.hidden, view.conditions, view.sort, kinds]);

  const numericKeys = useMemo(
    () => new Set(gridColumns.filter((column) => column.numeric).map((column) => column.key)),
    [gridColumns],
  );
  const grouped = useMemo(
    () =>
      groupRows(shownRows, view.groupBy, [...numericKeys], collapsed, (value) =>
        view.groupBy ? display({ [view.groupBy]: value }, { key: view.groupBy }) : value,
      ),
    // biome-ignore lint/correctness/useExhaustiveDependencies: `display` depends on `columns` only
    [shownRows, view.groupBy, numericKeys, collapsed],
  );
  const ids = useMemo(() => grouped.rows.map(idOf), [grouped.rows, idOf]);
  const part = selectedPart(selection, grouped.rows, gridColumns, idOf);
  const count = part.rows.length;
  const noun = (n: number) => `${n} ${n === 1 ? rowNoun[0] : rowNoun[1]}`;
  const selectionLabel = part.box
    ? `${part.box.r1 - part.box.r0 + 1} ${part.box.r1 === part.box.r0 ? "row" : "rows"} × ${part.box.c1 - part.box.c0 + 1} columns`
    : `${noun(count)} selected`;

  const valuesOf = (key: string) =>
    valueCounts(rows, key, (value) => display({ [key]: value }, { key }));
  const chips = filterChips(
    view,
    columns.map((column) => ({ key: column.key, label: column.label, money: column.money })),
    valuesOf,
  );
  const groupOptions = columns.filter((column) => types[column.key] === "choice");
  const totals = Object.fromEntries(
    gridColumns
      .filter((column) => column.numeric)
      .map((column) => [column.key, format(column.key, sumOf(shownRows, column.key))]),
  );

  // ── actions ────────────────────────────────────────────────────────────
  async function copyText(text: string, message: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      toast.error("Couldn't copy", {
        description: "The browser blocked the clipboard. Use Export instead.",
      });
    }
  }
  function copySelection() {
    if (count === 0) return;
    void copyText(
      toTsv(part.rows, part.columns, display),
      `Copied ${part.box ? selectionLabel : noun(count)} with headers — paste into Google Sheets`,
    );
  }
  function exportSelection() {
    if (count === 0) return;
    onExport(part.rows, part.box ? part.columns.map((column) => column.key) : null);
  }
  function showOnly() {
    if (count === 0) return;
    edit({ ...view, only: part.rows.map(idOf) });
    setSelection(EMPTY_SELECTION);
  }
  function setColumnFilter(key: string, hidden: string[], condition: SheetCondition | null) {
    const conditions = { ...view.conditions };
    if (condition) conditions[key] = condition;
    else delete conditions[key];
    edit({ ...view, hidden: { ...view.hidden, [key]: hidden }, conditions });
  }
  function removeChip(key: string) {
    if (key === ONLY_CHIP) edit({ ...view, only: null });
    else setColumnFilter(key, [], null);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const mod = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();
    if (mod && key === "c") {
      event.preventDefault();
      copySelection();
      return;
    }
    if (mod && key === "a") {
      event.preventDefault();
      setSelection(selectAll(ids));
      return;
    }
    if (event.key === "Escape") {
      setSelection(EMPTY_SELECTION);
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      setSelection((current) => toggleActiveRow(current, ids));
      return;
    }
    const step = STEP[event.key];
    if (!step) return;
    event.preventDefault();
    setSelection((current) =>
      moveActive(current, step[0], step[1], event.shiftKey, ids, gridColumns.length),
    );
  }

  // ── right-click and double-click ───────────────────────────────────────
  const menuColumn = menuCell ? gridColumns[menuCell.c] : undefined;
  const menuRow = menuCell ? grouped.rows[menuCell.r] : undefined;
  const menuValue = menuColumn && menuRow ? cellText(menuRow[menuColumn.key]) : null;
  const menuLabel = menuColumn && menuRow ? display(menuRow, menuColumn) || "(Blank)" : "";
  const menuIsChoice = menuColumn ? types[menuColumn.key] === "choice" : false;
  const peekColumn = peek ? gridColumns[peek.c] : undefined;
  const peekRow = peek ? grouped.rows[peek.r] : undefined;
  const peekText = peekColumn && peekRow ? display(peekRow, peekColumn) : "";
  const peekRaw = peekColumn && peekRow ? cellText(peekRow[peekColumn.key]) : "";

  const saveSummary =
    [
      ...chips.map((chip) => chip.label),
      view.groupBy ? `grouped by ${byKey.get(view.groupBy)?.label ?? view.groupBy}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "no filters yet";

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* The numbers that matter first (Leon, 2026-10-26: "no hierarchy"). */}
      <section
        aria-label="Summary"
        className="flex min-w-0 flex-wrap items-end gap-x-10 gap-y-3 pb-1"
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{capitalize(rowNoun[1])}</span>
          <strong className="font-mono text-2xl font-semibold tracking-tight tabular-nums">
            {loading ? "…" : shownRows.length.toLocaleString("en-PH")}
          </strong>
        </div>
        {(summaryKeys ?? []).flatMap((key) => {
          const column = byKey.get(key);
          if (!column) return [];
          return [
            <div className="flex flex-col gap-0.5" key={key}>
              <span className="text-xs text-muted-foreground">{column.label}</span>
              <strong className="font-mono text-2xl font-semibold tracking-tight tabular-nums">
                {loading ? "…" : format(key, sumOf(shownRows, key))}
              </strong>
            </div>,
          ];
        })}
        <div className="ml-auto flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {chips.find((chip) => byKey.get(chip.key)?.date)?.label.split(": ")[1] ?? "All time"} ·{" "}
            {shownRows.length === rows.length
              ? `every ${rowNoun[0]}`
              : `${rows.length - shownRows.length} hidden by filters`}
          </p>
          <Popover>
            <PopoverTrigger render={<Button size="sm" type="button" variant="ghost" />}>
              <KeyboardIcon /> Shortcuts
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-4">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                {SHORTCUTS.map(([keys, does]) => (
                  <div className="contents" key={keys}>
                    <dt className="font-mono text-xs whitespace-nowrap text-primary">{keys}</dt>
                    <dd className="text-muted-foreground">{does}</dd>
                  </div>
                ))}
              </dl>
            </PopoverContent>
          </Popover>
        </div>
      </section>
      <SheetToolbar
        activeView={activeView}
        chips={chips}
        columns={columns.map((column) => ({
          key: column.key,
          label: column.label,
          hidden: view.hiddenColumns.includes(column.key),
        }))}
        extra={extra}
        groupBy={view.groupBy}
        groupOptions={groupOptions}
        onClearFilters={() => edit(withoutFilters(view))}
        onDeleteView={(name) => {
          const next = saved.filter((entry) => entry.name !== name);
          setSaved(next);
          sheetStorage.saveSaved(storeKey, next);
          setActiveView(null);
          toast.success(`Deleted “${name}”`);
        }}
        onEditChip={(key) => {
          if (key === ONLY_CHIP) return;
          if (view.hiddenColumns.includes(key)) {
            edit({ ...view, hiddenColumns: view.hiddenColumns.filter((hidden) => hidden !== key) });
          }
          setOpenMenu(key);
        }}
        onExportAll={() => onExport([...shownRows], null)}
        onGroupBy={(key) => {
          setCollapsed(new Set());
          edit({ ...view, groupBy: key });
        }}
        onPickView={(name) => {
          const picked = saved.find((entry) => entry.name === name);
          if (picked) {
            setView(normalizeView(picked.view));
            setActiveView(picked.name);
            setCollapsed(new Set());
            setSelection(EMPTY_SELECTION);
          } else setActiveView(null);
        }}
        onRemoveChip={removeChip}
        onSaveView={(name) => {
          const next = [...saved.filter((entry) => entry.name !== name), { name, view }];
          setSaved(next);
          sheetStorage.saveSaved(storeKey, next);
          setActiveView(name);
          toast.success(`Saved “${name}”`, { description: "Pick it any time from the view list." });
        }}
        onSearch={setSearch}
        onToggleColumn={(key) =>
          edit({
            ...view,
            hiddenColumns: view.hiddenColumns.includes(key)
              ? view.hiddenColumns.filter((hidden) => hidden !== key)
              : [...view.hiddenColumns, key],
          })
        }
        saveSummary={saveSummary}
        saved={saved}
        search={search}
        shownRows={shownRows.length}
        totalRows={rows.length}
      />

      <div className="relative min-w-0" ref={gridArea}>
        <ContextMenu>
          <ContextMenuTrigger
            onContextMenuCapture={() => setMenuCell(null)}
            render={<div className="min-w-0" />}
          >
            <SheetGrid
              dense={dense}
              box={part.box}
              columns={gridColumns}
              display={display}
              empty={
                <Empty>
                  <EmptyHeader>
                    <EmptyTitle>{emptyTitle}</EmptyTitle>
                    <EmptyDescription>{emptyDescription}</EmptyDescription>
                  </EmptyHeader>
                  {chips.length > 0 ? (
                    <Button
                      onClick={() => edit(withoutFilters(view))}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      Clear filters
                    </Button>
                  ) : null}
                </Empty>
              }
              focus={selection.active}
              formatSum={format}
              headerMenu={(column) => (
                <SheetColumnMenu
                  condition={view.conditions[column.key] ?? null}
                  hidden={view.hidden[column.key] ?? []}
                  label={column.label}
                  onApply={({ condition, hidden }) =>
                    setColumnFilter(column.key, hidden, condition)
                  }
                  onOpenChange={(open) => setOpenMenu(open ? column.key : null)}
                  onSort={(dir) => edit({ ...view, sort: { key: column.key, dir } })}
                  open={openMenu === column.key}
                  selection={
                    count > 0
                      ? {
                          label: selectionLabel,
                          choices: selectionChoices(
                            grouped.rows,
                            column.key,
                            new Set(part.rows.map(idOf)),
                            idOf,
                            (value) => display({ [column.key]: value }, column),
                          ),
                          onSelect: (values, on) =>
                            setSelection((current) =>
                              withRows(
                                part.box
                                  ? { ...current, rows: new Set(part.rows.map(idOf)) }
                                  : current,
                                idsWithValues(grouped.rows, column.key, values, idOf),
                                on,
                              ),
                            ),
                          onShowOnly: showOnly,
                        }
                      : null
                  }
                  sort={view.sort?.key === column.key ? view.sort.dir : null}
                  type={types[column.key] ?? "text"}
                  values={valuesOf(column.key)}
                />
              )}
              isRowSelected={(index) => !part.box && selection.rows.has(ids[index] ?? "")}
              lines={grouped.lines}
              loading={loading}
              onCellContext={(r, c) => {
                setMenuCell({ r, c });
                const id = ids[r];
                const inSelection = part.box
                  ? r >= part.box.r0 && r <= part.box.r1 && c >= part.box.c0 && c <= part.box.c1
                  : id !== undefined && selection.rows.has(id);
                if (!inSelection)
                  setSelection(pressCell(selection, r, c, { shift: false, ctrl: false }, ids));
              }}
              onCellDown={(r, c, modifiers) => {
                dragStart.current = modifiers.shift || modifiers.ctrl ? null : { r, c };
                setSelection((current) => pressCell(current, r, c, modifiers, ids));
              }}
              onCellDrag={(r, c) => {
                const start = dragStart.current;
                if (start) setSelection((current) => dragTo(current, start, r, c, ids));
              }}
              onCellPeek={(r, c) => setPeek({ r, c })}
              onKeyDown={onKeyDown}
              onResize={(key, width) => edit({ ...view, widths: { ...view.widths, [key]: width } })}
              onSelectAll={() => setSelection(selectAll(ids))}
              onSelectColumn={(c) =>
                ids.length > 0
                  ? setSelection({
                      rows: new Set(),
                      box: { anchor: { r: 0, c }, focus: { r: ids.length - 1, c } },
                      anchor: 0,
                      active: { r: 0, c },
                    })
                  : undefined
              }
              onToggleGroup={(value) => {
                const next = new Set(collapsed);
                if (next.has(value)) next.delete(value);
                else next.add(value);
                setCollapsed(next);
              }}
              search={search}
              sort={view.sort}
              totalLabel={noun(shownRows.length)}
              totals={totals}
            />
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem disabled={count === 0} onClick={copySelection}>
              <CopyIcon /> Copy
              <ContextMenuShortcut>Ctrl+C</ContextMenuShortcut>
            </ContextMenuItem>
            {menuIsChoice && menuColumn && menuValue !== null ? (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem
                  onClick={() => {
                    const others = valuesOf(menuColumn.key)
                      .map((value) => value.value)
                      .filter((value) => value !== menuValue);
                    setColumnFilter(
                      menuColumn.key,
                      others,
                      view.conditions[menuColumn.key] ?? null,
                    );
                  }}
                >
                  <EyeIcon /> Show only “{menuLabel}”
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={() => {
                    const hidden = [
                      ...new Set([...(view.hidden[menuColumn.key] ?? []), menuValue]),
                    ];
                    setColumnFilter(
                      menuColumn.key,
                      hidden,
                      view.conditions[menuColumn.key] ?? null,
                    );
                  }}
                >
                  <EyeSlashIcon /> Hide “{menuLabel}”
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={() => {
                    setCollapsed(new Set());
                    edit({ ...view, groupBy: menuColumn.key });
                  }}
                >
                  <RowsIcon /> Group by {menuColumn.label}
                </ContextMenuItem>
              </>
            ) : null}
            {menuColumn && chips.some((chip) => chip.key === menuColumn.key) ? (
              <ContextMenuItem onClick={() => setColumnFilter(menuColumn.key, [], null)}>
                <FunnelSimpleXIcon /> Clear the {menuColumn.label} filter
              </ContextMenuItem>
            ) : null}
          </ContextMenuContent>
        </ContextMenu>

        {count > 0 ? (
          <SheetSelectionBar
            label={selectionLabel}
            onClear={() => setSelection(EMPTY_SELECTION)}
            onCopy={copySelection}
            onCopyTotal={(label, raw, text) => void copyText(raw, `${label} total copied: ${text}`)}
            onExport={exportSelection}
            onShowOnly={part.box ? null : showOnly}
            totals={columnTotals(
              part.rows,
              part.box || !summaryKeys
                ? part.columns
                : part.columns.filter((column) => summaryKeys.includes(column.key)),
              numericKeys,
            )
              // A bar that fits: four totals at most.
              .slice(0, 4)
              .map(({ column, total }) => ({
                key: column.key,
                label: column.label,
                text: format(column.key, total),
                raw: total.toFixed(2),
              }))}
          />
        ) : null}
      </div>

      <Dialog onOpenChange={(open) => (open ? undefined : setPeek(null))} open={peek !== null}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{peekColumn?.label ?? ""}</DialogTitle>
            <DialogDescription className="font-mono break-all text-foreground select-text">
              {peekText || "—"}
            </DialogDescription>
            {peekRaw && peekRaw !== peekText ? (
              <p className="font-mono text-xs break-all text-muted-foreground select-text">
                Stored as {peekRaw}
              </p>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPeek(null)} type="button" variant="outline">
              Close
            </Button>
            <Button
              onClick={() => {
                void copyText(peekText, `${peekColumn?.label ?? "Value"} copied`);
                setPeek(null);
              }}
              type="button"
            >
              <CopyIcon /> Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
